import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  calculateSimpleMovingAverage, findBreakoutCandleIndex, findPredictionEntryCandleIndex, getEntryTargetMeasurement,
  getEntryStopMeasurement, selectSignalChartCandles, filterSignalChartCandles,
} from '../lib/signalChartUtils';
import { getCachedSignalChartData, getSignalChartData } from '../lib/signalChartData';
import { formatScannedAge, subscribeToLivePriceClock } from '../lib/livePriceUtils';
import CopySymbol from './CopySymbol';

const toFinite = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const nyTimePartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const getSessionType = (iso) => {
  if (!iso) return 'after';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return 'after';

  const parts = nyTimePartsFormatter.formatToParts(dt);
  const hourPart = parts.find((p) => p.type === 'hour')?.value;
  const minutePart = parts.find((p) => p.type === 'minute')?.value;
  const hour = Number(hourPart);
  const minute = Number(minutePart);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 'after';

  const totalMinutes = hour * 60 + minute;
  const regularOpen = 9 * 60 + 30;
  const regularClose = 16 * 60;

  if (totalMinutes < regularOpen) return 'pre';
  if (totalMinutes < regularClose) return 'market';
  return 'after';
};

const sessionMeta = {
  pre: { label: 'Pre-Market', fill: '#bfdbfe', text: '#1d4ed8' },
  market: { label: 'Market Hours', fill: '#dcfce7', text: '#166534' },
  after: { label: 'After-Hours', fill: '#e5e7eb', text: '#374151' },
};

const formatSessionDate = (iso) => {
  if (!iso) return '-';
  try {
    const dt = new Date(iso);
    return dt.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).replace(/\bSep\b/, 'Sept');
  } catch {
    return iso;
  }
};

const formatTsLong = (iso) => {
  if (!iso) return '-';
  try {
    const dt = new Date(iso);
    return dt.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).replace(/\bSep\b/, 'Sept');
  } catch {
    return iso;
  }
};

const formatCompactNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${Math.round(n)}`;
};

const lineColorByKey = {
  entry: '#2563eb',
  stop: '#dc2626',
  target: '#16a34a',
};

const lineLabelByKey = {
  entry: 'Entry',
  stop: 'Stop',
  target: 'Target',
};

const MIN_BODY_HEIGHT_PX = 2;
const MIN_WICK_HEIGHT_PX = 6;
const MIN_WICK_OUTSIDE_BODY_PX = 1;
const MIN_VISIBLE_BARS = 20;
const DEFAULT_VISIBLE_BARS = 140;
const MIN_X_STEP_PX = 5;
const MAX_X_STEP_PX = 6.5;
const X_ZOOM_FACTOR = 0.78;
const LIVE_REFRESH_MS = 5000;
const VOLUME_PANE_RATIO = 0.22;
const SMA_STUDIES = [
  { period: 200, color: '#16a34a' },
  { period: 20, color: '#eab308' },
  { period: 21, color: '#2563eb' },
  { period: 8, color: '#dc2626' },
];

const DEFAULT_CHART_HEIGHT = 560;
const LARGE_SCREEN_CHART_HEIGHT = 760;
const PRICE_LABEL_WIDTH = 72;
const MEASUREMENT_LANE_WIDTH = 48;
const LIVE_LABEL_LANE_WIDTH = 110;
const margin = { top: 24, right: PRICE_LABEL_WIDTH + MEASUREMENT_LANE_WIDTH + LIVE_LABEL_LANE_WIDTH, bottom: 16, left: 8 };

const scrollBarStyles = `
  .signal-chart-scrollbars {
    scrollbar-width: auto;
    scrollbar-color: #9ca3af #e5e7eb;
  }

  .signal-chart-scrollbars::-webkit-scrollbar {
    height: 12px;
  }

  .signal-chart-scrollbars::-webkit-scrollbar-track {
    background: #e5e7eb;
    border-radius: 9999px;
  }

  .signal-chart-scrollbars::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 9999px;
    border: 2px solid #e5e7eb;
  }

  .signal-chart-scrollbars::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
`;

const toIntervalMs = (interval) => {
  const raw = String(interval || '').trim().toLowerCase();
  if (!raw) return 5 * 60 * 1000;

  const match = raw.match(/^(\d+)(m|h|d|wk|mo)$/);
  if (!match) return 5 * 60 * 1000;

  const value = Number(match[1]);
  const unit = match[2];
  if (!Number.isFinite(value) || value <= 0) return 5 * 60 * 1000;

  if (unit === 'm') return value * 60 * 1000;
  if (unit === 'h') return value * 60 * 60 * 1000;
  if (unit === 'd') return value * 24 * 60 * 60 * 1000;
  if (unit === 'wk') return value * 7 * 24 * 60 * 60 * 1000;
  if (unit === 'mo') return value * 30 * 24 * 60 * 60 * 1000;
  return 5 * 60 * 1000;
};

const updateCandlesWithQuote = (candles, quote, interval, includeExtendedHours) => {
  if (!Array.isArray(candles) || candles.length === 0 || !quote) return candles;

  const latestPrice = toFinite(quote.price);
  if (latestPrice === null) return candles;

  const quoteTsRaw = quote.source_timestamp || quote.timestamp;
  const quoteTs = new Date(quoteTsRaw);
  if (Number.isNaN(quoteTs.getTime())) return candles;
  if (!filterSignalChartCandles([{ time: quoteTs.toISOString() }], interval, includeExtendedHours).length) return candles;

  const updated = [...candles]
    .map((c) => ({ ...c }))
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  const intervalMs = toIntervalMs(interval);
  const last = updated[updated.length - 1];
  const lastTs = new Date(last.time);
  if (Number.isNaN(lastTs.getTime())) return candles;

  const quoteInterval = String(interval || '').toLowerCase();
  const intervalAggregate = toFinite(quote.volume_by_interval?.[quoteInterval]);
  const volumeMatchesChart =
    quote.volume_type === 'bar'
    && String(quote.volume_interval || '').toLowerCase() === quoteInterval;
  const liveVolume = intervalAggregate !== null
    ? intervalAggregate
    : (volumeMatchesChart ? toFinite(quote.volume) : null);
  const quoteVolume = liveVolume !== null ? Math.max(0, liveVolume) : null;

  if (quoteTs.getTime() - lastTs.getTime() >= intervalMs * 0.98) {
    const nextOpen = toFinite(last.close) ?? latestPrice;
    updated.push({
      time: quoteTs.toISOString(),
      open: nextOpen,
      high: latestPrice,
      low: latestPrice,
      close: latestPrice,
      volume: quoteVolume,
    });
    return updated;
  }

  last.close = latestPrice;
  const prevHigh = toFinite(last.high);
  const prevLow = toFinite(last.low);
  last.high = prevHigh === null ? latestPrice : Math.max(prevHigh, latestPrice);
  last.low = prevLow === null ? latestPrice : Math.min(prevLow, latestPrice);
  if (quoteVolume !== null) {
    const prevVolume = toFinite(last.volume);
    last.volume = prevVolume === null ? quoteVolume : Math.max(prevVolume, quoteVolume);
  }

  return updated;
};

const isTypingElement = (target) => {
  if (!target) return false;
  const tag = String(target.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
};

export default function SignalChartModal({ scan, liveQuote, onClose }) {
  const [loading, setLoading] = useState(() => !getCachedSignalChartData(scan));
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(() => {
    const cached = getCachedSignalChartData(scan);
    if (!cached) return null;
    const candles = filterSignalChartCandles(cached.candles || [], cached.interval || scan?.interval, cached.include_extended_hours ?? scan?.include_extended_hours);
    return { ...cached, candles, point_count: candles.length };
  });
  const [liveTick, setLiveTick] = useState(0);
  const [chartWidth, setChartWidth] = useState(1200);
  const [chartHeight, setChartHeight] = useState(DEFAULT_CHART_HEIGHT);
  const [zoomBars, setZoomBars] = useState(100);
  const [yZoomScale, setYZoomScale] = useState(1);
  const [panOffsetBars, setPanOffsetBars] = useState(0);
  const [scaleMode, setScaleMode] = useState('fit');
  const panStateRef = useRef(null);
  const [hoveredCandle, setHoveredCandle] = useState(null);
  const [mouseGuideY, setMouseGuideY] = useState(null);
  const [now, setNow] = useState(Date.now);
  const chartContainerRef = useRef(null);
  const initialScrollKeyRef = useRef(null);
  const liveQuoteRef = useRef(null);
  const fetchInFlightRef = useRef(false);

  const totalBars = chartData?.candles?.length || 0;
  const hasChartCandles = totalBars > 0;

  useEffect(() => subscribeToLivePriceClock(setNow), []);

  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.innerText = scrollBarStyles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  useEffect(() => {
    if (totalBars > 0) {
      const intervalRaw = String(scan?.interval || chartData?.interval || '').trim().toLowerCase();
      const defaultVisibleBars = intervalRaw === '1m' ? 90 : DEFAULT_VISIBLE_BARS;
      const barsToFillWidth = Math.ceil(
        Math.max(1, chartWidth - margin.left - margin.right) / MAX_X_STEP_PX
      ) + 1;
      setZoomBars(Math.max(MIN_VISIBLE_BARS, Math.min(totalBars, Math.max(defaultVisibleBars, barsToFillWidth))));
      setYZoomScale(1);
      setPanOffsetBars(0);
    }
  }, [totalBars, scan?.id, scan?.interval, chartData?.interval, chartWidth]);

  useEffect(() => {
    setLiveTick(0);
    liveQuoteRef.current = null;
  }, [scan?.id]);

  const fetchChart = useCallback(async (showSpinner = false) => {
    if (!scan?.id) return;
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    if (showSpinner && !getCachedSignalChartData(scan)) {
      setLoading(true);
    }

    try {
      const raw = await getSignalChartData(scan, { forceRefresh: !showSpinner });
      const nextInterval = raw?.interval || scan?.interval;
      const includeExtendedHours = raw?.include_extended_hours ?? scan?.include_extended_hours;
      let nextCandles = filterSignalChartCandles(raw?.candles || [], nextInterval, includeExtendedHours);
      if (liveQuoteRef.current) {
        nextCandles = updateCandlesWithQuote(nextCandles, liveQuoteRef.current, nextInterval, includeExtendedHours);
      }
      setChartData({
        ...raw,
        candles: nextCandles,
        point_count: nextCandles.length,
      });
      setError('');
      setLiveTick(Date.now());
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to load chart data.');
    } finally {
      fetchInFlightRef.current = false;
      if (showSpinner) {
        setLoading(false);
      }
    }
  }, [scan?.id, scan?.interval, scan?.period, scan?.include_extended_hours]);

  useEffect(() => {
    if (!scan?.id) return;
    let intervalId = null;
    fetchInFlightRef.current = false;
    fetchChart(true);
    intervalId = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchChart(false);
    }, LIVE_REFRESH_MS);

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [scan?.id, fetchChart]);

  useEffect(() => {
    if (!liveQuote || !scan?.id) return;
    liveQuoteRef.current = liveQuote;
    setChartData((prev) => {
      if (!prev || !Array.isArray(prev.candles) || prev.candles.length === 0) return prev;
      const nextCandles = updateCandlesWithQuote(prev.candles, liveQuote, prev.interval || scan?.interval, prev.include_extended_hours ?? scan?.include_extended_hours);
      if (nextCandles === prev.candles) return prev;
      return {
        ...prev,
        candles: nextCandles,
        point_count: nextCandles.length,
        as_of: liveQuote.timestamp || new Date().toISOString(),
      };
    });
    setLiveTick(Date.now());
  }, [liveQuote, scan?.id, scan?.interval, scan?.include_extended_hours]);

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;

    const updateSize = () => {
      const nextWidth = Math.max(320, Math.floor(el.clientWidth));
      setChartWidth(nextWidth);
      const nextChartHeight = window.innerWidth >= 1024 && window.innerHeight >= 800
        ? Math.min(LARGE_SCREEN_CHART_HEIGHT, window.innerHeight - 100)
        : DEFAULT_CHART_HEIGHT;
      setChartHeight(nextChartHeight);
    };

    updateSize();
    window.addEventListener('resize', updateSize);

    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', updateSize);
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(el);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, [hasChartCandles]);

  const plot = useMemo(() => {
    const safeCandles = filterSignalChartCandles(chartData?.candles || [], chartData?.interval || scan?.interval, chartData?.include_extended_hours ?? scan?.include_extended_hours);
    const allCandles = [...safeCandles].sort((a, b) => {
      const ta = new Date(a?.time || 0).getTime();
      const tb = new Date(b?.time || 0).getTime();
      return ta - tb;
    });
    if (!allCandles.length) {
      return {
        candles: [],
        smaLines: [],
        overlays: [],
        priceMin: 0,
        priceMax: 1,
        volumeMax: 0,
        totalCandles: 0,
        visibleCandles: 0,
      };
    }

    const visibleCount = Math.min(allCandles.length, Math.max(MIN_VISIBLE_BARS, zoomBars));
    const maxOffset = Math.max(0, allCandles.length - visibleCount);
    const clampedOffset = Math.min(maxOffset, Math.max(0, panOffsetBars));
    const windowEndExclusive = allCandles.length - clampedOffset;
    const windowStart = Math.max(0, windowEndExclusive - visibleCount);
    const visibleWindowCandles = allCandles.slice(windowStart, windowEndExclusive);
    const visibleSessionCounts = {};
    visibleWindowCandles.forEach(candle => {
      const key = `${getSessionType(candle.time)}|${formatSessionDate(candle.time)}`;
      visibleSessionCounts[key] = (visibleSessionCounts[key] || 0) + 1;
    });

    const perfInnerWidth = Math.max(1, chartWidth - margin.left - margin.right);
    const maxRenderCandles = Math.max(100, Math.floor(perfInnerWidth / 3));
    const includeExtendedHours = chartData?.include_extended_hours ?? scan?.include_extended_hours;
    // Extended sessions can be sparse: keep every returned candle and let the
    // existing horizontal scrollbar handle a window wider than the viewport.
    const renderStep = includeExtendedHours
      ? 1
      : Math.max(1, Math.ceil(visibleWindowCandles.length / maxRenderCandles));
    const entryIndex = findPredictionEntryCandleIndex(allCandles, chartData?.entry_price, {
      confirmationTime: scan?.confirmation_time, retestTime: scan?.retest_time,
    });
    const breakoutIndex = findBreakoutCandleIndex(allCandles, scan?.breakout_time);
    const xLabelTimes = new Set(
      visibleWindowCandles
        .filter((candle, index) => index === 0 || index === visibleWindowCandles.length - 1
          || getSessionType(candle.time) !== getSessionType(visibleWindowCandles[index - 1].time)
          || formatSessionDate(candle.time) !== formatSessionDate(visibleWindowCandles[index - 1].time)
          || getSessionType(candle.time) !== getSessionType(visibleWindowCandles[index + 1].time)
          || formatSessionDate(candle.time) !== formatSessionDate(visibleWindowCandles[index + 1].time))
        .map(candle => candle.time)
    );
    if (breakoutIndex >= 0) xLabelTimes.add(allCandles[breakoutIndex].time);
    const { candles, entryIndex: visibleEntryIndex } = selectSignalChartCandles(
      allCandles, windowStart, windowEndExclusive, renderStep, entryIndex, xLabelTimes
    );

    const firstCandleTime = new Date(allCandles[0].time).getTime();
    const warmupCandles = [...filterSignalChartCandles(chartData?.sma_warmup_candles || [], chartData?.interval || scan?.interval, chartData?.include_extended_hours ?? scan?.include_extended_hours)]
      .filter((candle) => new Date(candle?.time).getTime() < firstCandleTime)
      .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
      .slice(-200);
    const indicatorCandles = [...warmupCandles, ...allCandles];
    const smaLines = SMA_STUDIES.map(({ period, color }) => {
      const smaValues = calculateSimpleMovingAverage(indicatorCandles, period);
      const smaByTime = new Map(indicatorCandles.map((candle, index) => [candle.time, smaValues[index]]));
      const segments = [];
      let currentSegment = [];
      candles.forEach((candle, index) => {
        const value = smaByTime.get(candle.time);
        if (value === null || value === undefined) {
          if (currentSegment.length > 1) segments.push(currentSegment);
          currentSegment = [];
          return;
        }
        currentSegment.push({ index, value });
      });
      if (currentSegment.length > 1) segments.push(currentSegment);
      return { period, color, segments };
    }).filter(({ segments }) => segments.length > 0);

    const candlePrices = [];
    candles.forEach((c) => {
      const high = toFinite(c.high);
      const low = toFinite(c.low);
      if (high !== null) candlePrices.push(high);
      if (low !== null) candlePrices.push(low);
    });
    smaLines.forEach(({ segments }) => {
      segments.forEach((segment) => {
        segment.forEach(({ value }) => candlePrices.push(value));
      });
    });

    if (!candlePrices.length) {
      return {
        candles: [],
        smaLines: [],
        overlays: [],
        priceMin: 0,
        priceMax: 1,
        volumeMax: 0,
        totalCandles: allCandles.length,
        visibleCandles: 0,
      };
    }

    const volumeMax = candles.reduce((max, candle) => {
      const volume = toFinite(candle.volume);
      if (volume === null || volume < 0) return max;
      return Math.max(max, volume);
    }, 0);

    const entry = toFinite(chartData?.entry_price);
    const stop = toFinite(chartData?.stop_price);
    const target = toFinite(chartData?.target_price);
    const signalPrices = [entry, stop, target].filter((price) => price !== null);

    const candleMin = Math.min(...candlePrices);
    const candleMax = Math.max(...candlePrices);
    const candleRange = Math.max(candleMax - candleMin, 0.01);
    // Use price-relative minimum padding so low-priced stocks aren't over-stretched.
    const midPrice = (candleMin + candleMax) / 2;
    const padding = Math.max(candleRange * 0.15, midPrice * 0.003);

    let min = candleMin - padding;
    let max = candleMax + padding;

    if (scaleMode === 'include_set' && signalPrices.length > 0) {
      min = Math.min(min, Math.min(...signalPrices) - padding);
      max = Math.max(max, Math.max(...signalPrices) + padding);
    } else if (scaleMode === 'auto' && signalPrices.length > 0) {
      const signalMin = Math.min(...signalPrices);
      const signalMax = Math.max(...signalPrices);
      const signalBelow = Math.max(0, min - signalMin);
      const signalAbove = Math.max(0, signalMax - max);
      const blend = 0.38;

      min -= signalBelow * blend;
      max += signalAbove * blend;

      // Preserve a minimum visible range so candles don't get flattened.
      const blendedRange = Math.max(max - min, candleRange * 1.9);
      const centeredMin = candleMin - blendedRange * 0.45;
      const centeredMax = candleMax + blendedRange * 0.45;
      min = Math.min(min, centeredMin);
      max = Math.max(max, centeredMax);
    }

    const overlays = [
      { key: 'entry', value: entry },
      { key: 'stop', value: stop },
      { key: 'target', value: target },
    ].filter((o) => o.value !== null);

    const center = (min + max) / 2;
    const halfRange = ((max - min) / 2) * yZoomScale;
    min = center - halfRange;
    max = center + halfRange;

    return {
      candles,
      visibleSessionCounts,
      smaLines,
      xLabelTimes,
      entryIndex: visibleEntryIndex,
      breakoutIndex: breakoutIndex >= 0 ? candles.indexOf(allCandles[breakoutIndex]) : -1,
      overlays,
      priceMin: min - padding * 0.5,
      priceMax: max + padding * 0.5,
      volumeMax,
      totalCandles: allCandles.length,
      visibleCandles: visibleWindowCandles.length,
      renderedCandles: candles.length,
      clampedOffset,
    };
  }, [chartData, zoomBars, yZoomScale, panOffsetBars, scaleMode, chartWidth, scan?.retest_time, scan?.confirmation_time, scan?.breakout_time, scan?.interval, scan?.include_extended_hours]);

  const handleZoomIn = () => {
    setZoomBars((prev) => Math.max(MIN_VISIBLE_BARS, Math.floor(prev * X_ZOOM_FACTOR)));
  };

  const handleZoomOut = () => {
    setZoomBars((prev) => {
      const next = Math.ceil(prev / X_ZOOM_FACTOR);
      return totalBars > 0 ? Math.min(totalBars, next) : next;
    });
  };

  const handleZoomReset = () => {
    if (totalBars > 0) {
      setZoomBars(totalBars);
    }
    setYZoomScale(1);
    setPanOffsetBars(0);
  };

  const handleChartWheel = (event) => {
    event.preventDefault();
    const zoomIn = event.deltaY < 0;

    if (event.shiftKey) {
      setYZoomScale((prev) => {
        const next = zoomIn ? prev * 0.88 : prev * 1.12;
        return Math.max(0.35, Math.min(3, next));
      });
      return;
    }

    if (!totalBars) return;

    const container = chartContainerRef.current;
    const rect = container?.querySelector('svg')?.getBoundingClientRect();
    const cursorX = rect ? event.clientX - rect.left : 0;
    const cursorPlotX = cursorX;
    const relative = Math.max(0, Math.min(1, (cursorPlotX - margin.left) / plotInnerWidth));

    const oldVisible = Math.min(totalBars, Math.max(MIN_VISIBLE_BARS, zoomBars));
    const newVisible = zoomIn
      ? Math.max(MIN_VISIBLE_BARS, Math.floor(oldVisible * X_ZOOM_FACTOR))
      : Math.min(totalBars, Math.ceil(oldVisible / X_ZOOM_FACTOR));

    const maxOldOffset = Math.max(0, totalBars - oldVisible);
    const oldOffset = Math.min(maxOldOffset, Math.max(0, panOffsetBars));
    const oldStart = totalBars - oldOffset - oldVisible;
    const focusIndex = oldStart + Math.floor(relative * Math.max(1, oldVisible - 1));

    const newStartUnclamped = focusIndex - Math.floor(relative * Math.max(1, newVisible - 1));
    const maxNewStart = Math.max(0, totalBars - newVisible);
    const newStart = Math.max(0, Math.min(maxNewStart, newStartUnclamped));
    const newOffset = totalBars - (newStart + newVisible);

    setZoomBars(newVisible);
    setPanOffsetBars(Math.max(0, newOffset));
  };

  const handlePanStart = (event) => {
    if (!event.isPrimary || event.button !== 0) return;
    if (!plot.totalCandles) return;
    if (event.pointerType !== 'touch' && plot.visibleCandles >= plot.totalCandles) return;
    panStateRef.current = {
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      startX: event.clientX,
      startOffset: plot.clampedOffset || 0,
      startScrollLeft: event.currentTarget.scrollLeft,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setHoveredCandle(null);
    setMouseGuideY(null);
  };

  const handlePanMove = (event) => {
    if (panStateRef.current?.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - panStateRef.current.startX;
    const barsPerPixel = plot.visibleCandles / Math.max(1, plotInnerWidth);
    const maxOffset = Math.max(0, plot.totalCandles - plot.visibleCandles);
    if (panStateRef.current.pointerType === 'touch') {
      // Negative positions pan history; positive positions reveal the right-hand price columns.
      const position = panStateRef.current.startScrollLeft
        - panStateRef.current.startOffset / barsPerPixel - deltaX;
      const maxScroll = Math.max(0, event.currentTarget.scrollWidth - event.currentTarget.clientWidth);
      event.currentTarget.scrollLeft = Math.max(0, Math.min(maxScroll, position));
      setPanOffsetBars(Math.max(0, Math.min(maxOffset, Math.round(-position * barsPerPixel))));
      return;
    }
    const deltaBars = Math.round(deltaX * barsPerPixel);
    const nextOffset = Math.max(0, Math.min(maxOffset, panStateRef.current.startOffset + deltaBars));
    setPanOffsetBars(nextOffset);
  };

  const handlePanEnd = (event) => {
    if (panStateRef.current?.pointerId !== event.pointerId) return;
    panStateRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handlePanBars = (direction) => {
    const step = Math.max(5, Math.floor(plot.visibleCandles * 0.2));
    const maxOffset = Math.max(0, plot.totalCandles - plot.visibleCandles);
    setPanOffsetBars((prev) => {
      const next = prev + direction * step;
      return Math.max(0, Math.min(maxOffset, next));
    });
  };

  const handleYZoomIn = () => {
    setYZoomScale((prev) => Math.max(0.35, prev * 0.88));
  };

  const handleYZoomOut = () => {
    setYZoomScale((prev) => Math.min(3, prev * 1.12));
  };

  const handleYZoomReset = () => {
    setYZoomScale(1);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isTypingElement(event.target)) return;

      const key = event.key;
      const lower = String(key || '').toLowerCase();

      if (key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (key === '+' || key === '=') {
        event.preventDefault();
        handleZoomIn();
        return;
      }

      if (key === '-' || key === '_') {
        event.preventDefault();
        handleZoomOut();
        return;
      }

      if (key === 'ArrowLeft') {
        event.preventDefault();
        handlePanBars(-1);
        return;
      }

      if (key === 'ArrowRight') {
        event.preventDefault();
        handlePanBars(1);
        return;
      }

      if (event.altKey && key === 'ArrowUp') {
        event.preventDefault();
        handleYZoomIn();
        return;
      }

      if (event.altKey && key === 'ArrowDown') {
        event.preventDefault();
        handleYZoomOut();
        return;
      }

      if (key === '0') {
        event.preventDefault();
        handleZoomReset();
        return;
      }

      if (lower === 'a') {
        event.preventDefault();
        setScaleMode('auto');
        return;
      }

      if (lower === 'f') {
        event.preventDefault();
        setScaleMode('fit');
        return;
      }

      if (lower === 'i') {
        event.preventDefault();
        setScaleMode('include_set');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, handleZoomIn, handleZoomOut, handlePanBars, handleYZoomIn, handleYZoomOut, handleZoomReset]);

  const viewportInnerWidth = Math.max(1, chartWidth - margin.left - margin.right);
  const plotInnerWidth = plot.candles.length > 1
    ? Math.max(
      (plot.candles.length - 1) * MIN_X_STEP_PX,
      Math.min(viewportInnerWidth, (plot.candles.length - 1) * MAX_X_STEP_PX)
    )
    : viewportInnerWidth;
  const svgWidth = margin.left + plotInnerWidth + margin.right;
  const hasCandles = plot.candles.length > 0;
  useLayoutEffect(() => {
    const container = chartContainerRef.current;
    const scanKey = JSON.stringify([scan?.id, scan?.period, scan?.interval]);
    if (!container || !hasCandles || initialScrollKeyRef.current === scanKey) return;
    if (chartWidth !== Math.max(320, Math.floor(container.clientWidth))) return;

    container.scrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    initialScrollKeyRef.current = scanKey;
  }, [scan?.id, scan?.period, scan?.interval, chartWidth, svgWidth, hasCandles]);

  const innerHeight = Math.max(1, chartHeight - margin.top - margin.bottom);
  const volumePaneHeight = Math.max(72, Math.floor(innerHeight * VOLUME_PANE_RATIO));
  const pricePaneHeight = Math.max(120, innerHeight - volumePaneHeight);
  const volumePaneTop = margin.top + pricePaneHeight;
  const volumePaneBottom = volumePaneTop + volumePaneHeight;

  const xStep = plot.candles.length > 1 ? plotInnerWidth / (plot.candles.length - 1) : plotInnerWidth;
  const bodyWidth = Math.max(1, Math.min(5, xStep - 1.5));
  const volumeColumnWidth = Math.max(3, Math.min(16, xStep * 0.72));
  const volumeBarWidth = Math.max(2, Math.min(14, volumeColumnWidth * 0.9));

  const yScale = (price) => {
    if (plot.priceMax === plot.priceMin) return margin.top + innerHeight / 2;
    const ratio = (price - plot.priceMin) / (plot.priceMax - plot.priceMin);
    return margin.top + pricePaneHeight - ratio * pricePaneHeight;
  };

  const clampY = (y) => Math.min(margin.top + pricePaneHeight, Math.max(margin.top, y));
  const mouseGuidePrice = mouseGuideY !== null && mouseGuideY <= volumePaneTop
    ? (plot.priceMax - (mouseGuideY - margin.top) / pricePaneHeight
      * (plot.priceMax - plot.priceMin)).toFixed(2)
    : null;
  const measurementColumnX = margin.left + plotInnerWidth + PRICE_LABEL_WIDTH
    + MEASUREMENT_LANE_WIDTH / 2;
  const liveLabelX = margin.left + plotInnerWidth + PRICE_LABEL_WIDTH + MEASUREMENT_LANE_WIDTH + 8;
  const liveLevel = (() => {
    if (panOffsetBars !== 0 || !plot.candles.length) return null;
    const lastIdx = plot.candles.length - 1;
    const candle = plot.candles[lastIdx];
    const price = toFinite(candle?.close);
    const open = toFinite(candle?.open);
    if (price === null || open === null) return null;

    return {
      x: margin.left + lastIdx * xStep,
      y: clampY(yScale(price)),
      color: price >= open ? '#16a34a' : '#dc2626',
      price: price.toFixed(2),
      labelX: liveLabelX,
    };
  })();
  const volumeScale = (value) => {
    const volume = Math.max(0, value || 0);
    if (!plot.volumeMax) return volumePaneBottom;
    const ratio = Math.min(1, volume / plot.volumeMax);
    return volumePaneBottom - ratio * volumePaneHeight;
  };

  const yTicks = 6;
  const gridLines = Array.from({ length: yTicks }, (_, i) => {
    const ratio = i / (yTicks - 1);
    const value = plot.priceMax - ratio * (plot.priceMax - plot.priceMin);
    return {
        y: margin.top + ratio * pricePaneHeight,
      value,
    };
  });

  const setCalculatedIso = scan?.scan_timestamp || scan?.status_datetime || scan?.created_at || null;

  const setMarker = useMemo(() => {
    if (!setCalculatedIso || !plot.candles.length) return null;

    const targetTs = new Date(setCalculatedIso).getTime();
    if (Number.isNaN(targetTs)) return null;

    let bestIdx = -1;
    let bestDiff = Number.POSITIVE_INFINITY;

    for (let i = 0; i < plot.candles.length; i += 1) {
      const candleTs = new Date(plot.candles[i]?.time).getTime();
      if (Number.isNaN(candleTs)) continue;
      const diff = Math.abs(candleTs - targetTs);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    }

    if (bestIdx < 0) return null;

    return {
      index: bestIdx,
      x: margin.left + bestIdx * xStep,
      time: plot.candles[bestIdx]?.time,
    };
  }, [setCalculatedIso, plot.candles, xStep]);

  const entryMarker = useMemo(() => {
    const ep = toFinite(chartData?.entry_price);
    const bestIdx = plot.entryIndex;
    if (!Number.isInteger(bestIdx) || bestIdx < 0 || ep === null) return null;
    return {
      index: bestIdx,
      x: margin.left + bestIdx * xStep,
      price: ep,
    };
  }, [chartData?.entry_price, plot.entryIndex, xStep]);

  const latestCandle = plot.candles.length ? plot.candles[plot.candles.length - 1] : null;
  const latestPrice = toFinite(latestCandle?.close);
  const latestVolume = toFinite(latestCandle?.volume);

  const entryPrice = toFinite(chartData?.entry_price);
  const stopPrice = toFinite(chartData?.stop_price);
  const targetPrice = toFinite(chartData?.target_price);
  const entryTargetMeasurement = getEntryTargetMeasurement(
    chartData?.entry_price,
    chartData?.target_price,
    yScale,
    margin.top,
    margin.top + pricePaneHeight
  );
  const entryStopMeasurement = getEntryStopMeasurement(
    chartData?.entry_price,
    chartData?.stop_price,
    yScale,
    margin.top,
    margin.top + pricePaneHeight
  );

  const distanceFromEntryPct = latestPrice !== null && entryPrice !== null && entryPrice !== 0
    ? ((latestPrice - entryPrice) / entryPrice) * 100
    : null;
  const distanceToTargetPct = latestPrice !== null && targetPrice !== null && latestPrice !== 0
    ? ((targetPrice - latestPrice) / latestPrice) * 100
    : null;
  const distanceToStopPct = latestPrice !== null && stopPrice !== null && latestPrice !== 0
    ? ((latestPrice - stopPrice) / latestPrice) * 100
    : null;

  const formatPct = (value) => {
    if (!Number.isFinite(value)) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const sessionSpans = useMemo(() => {
    if (!plot.candles.length) return [];

    const spans = [];
    let current = getSessionType(plot.candles[0]?.time);
    let currentDate = formatSessionDate(plot.candles[0]?.time);
    let startIdx = 0;

    for (let i = 1; i < plot.candles.length; i += 1) {
      const nextSession = getSessionType(plot.candles[i]?.time);
      const nextDate = formatSessionDate(plot.candles[i]?.time);
      if (nextSession !== current || nextDate !== currentDate) {
        spans.push({ session: current, date: currentDate, start: startIdx, end: i - 1 });
        current = nextSession;
        currentDate = nextDate;
        startIdx = i;
      }
    }

    spans.push({ session: current, date: currentDate, start: startIdx, end: plot.candles.length - 1 });
    return spans;
  }, [plot.candles]);

  const panMaxBars = Math.max(0, plot.totalCandles - plot.visibleCandles);
  const sliderValue = Math.max(0, Math.min(panMaxBars, panMaxBars - Math.round(panOffsetBars)));

  const handleScrollBarChange = (event) => {
    const next = Number(event.target.value);
    if (!Number.isFinite(next)) return;
    const clamped = Math.max(0, Math.min(panMaxBars, Math.round(next)));
    const mappedOffset = panMaxBars - clamped;
    setPanOffsetBars(mappedOffset);
  };

  const scanTime = scan?.scan_timestamp
    && Number.isFinite(new Date(scan.scan_timestamp).getTime())
    ? `${formatTsLong(scan.scan_timestamp)} ET`
    : 'Unavailable';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-1 sm:p-2">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[96rem] max-h-[calc(100dvh-1rem)] flex flex-col overflow-hidden">
        <div className="px-2 sm:px-3 py-2 shrink-0 border-b border-gray-200 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-base sm:text-lg font-bold text-gray-900">
              <CopySymbol symbol={scan.symbol} />
              <span className={`text-sm sm:text-base ${scan.signal_type === 'CALL' ? 'text-green-600' : 'text-red-600'}`}>
                {scan.signal_type === 'CALL' ? 'Call' : 'Put'}
              </span>
              <svg
                className={`h-4 w-4 shrink-0 ${scan.signal_type === 'CALL' ? 'text-green-600' : 'text-red-600'}`}
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                aria-label={scan.signal_type === 'CALL' ? 'Call signal' : 'Put signal'}
              >
                {scan.signal_type === 'CALL' ? (
                  <>
                    <path d="M3 16.5h14M4.5 13l3.2-3.2 2.5 2.3 5.3-6" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12.8 6.1H15.5v2.7" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </>
                ) : (
                  <>
                    <path d="M3 16.5h14M4.5 7l3.2 3.2 2.5-2.3 5.3 6" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12.8 13.9H15.5v-2.7" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </>
                )}
              </svg>
              <span className="text-xs font-normal text-gray-600" title={chartData?.sma_warmup_error || undefined}>
                Period: {scan.period || '-'} • Interval: {chartData?.interval || scan.interval || '-'}
                {' • '}Scan Time: {scanTime}
                {' • '}Scan Age: {formatScannedAge(scan?.scan_timestamp, now)}
                {' • '}{(chartData?.include_extended_hours ?? scan?.include_extended_hours) ? 'All market hours' : 'Regular market hours'}
                {plot.smaLines.map(({ period, color }) => (
                  <span key={`sma-label-${period}`} style={{ color }} className="font-semibold">
                    {' • '}SMA {period}
                  </span>
                ))}
              </span>
            </h2>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 text-gray-500 hover:text-gray-900 text-2xl leading-none"
            aria-label="Close chart modal"
          >
            ×
          </button>
        </div>

        <div className="min-h-0 overflow-y-auto space-y-1">
          {loading && !chartData ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : error && !chartData ? (
            <div className="bg-red-100 text-red-800 px-4 py-3 rounded">{error}</div>
          ) : !plot.candles.length ? (
            <div className="bg-amber-100 text-amber-800 px-4 py-3 rounded">No candle data returned from Yahoo Finance.</div>
          ) : (
            <>
              {error && (
                <div className="bg-red-100 text-red-800 px-4 py-3 rounded">
                  {error} Showing the last loaded chart data.
                </div>
              )}
              <div
                ref={chartContainerRef}
                onWheel={handleChartWheel}
                onPointerDown={handlePanStart}
                onPointerMove={handlePanMove}
                onPointerUp={handlePanEnd}
                onPointerCancel={handlePanEnd}
                onLostPointerCapture={handlePanEnd}
                className="signal-chart-scrollbars w-full overflow-x-scroll overflow-y-hidden bg-white cursor-default"
                style={{ WebkitOverflowScrolling: 'touch', scrollbarGutter: 'stable both-edges', touchAction: 'pan-y' }}
              >
                <svg
                  className="block ml-auto"
                  width={svgWidth}
                  height={chartHeight}
                  role="img"
                  aria-label="Signal candlestick chart"
                  onMouseMove={(event) => {
                    const rect = event.currentTarget.getBoundingClientRect();
                    if (rect.width <= 0 || rect.height <= 0) return;
                    const x = (event.clientX - rect.left) * svgWidth / rect.width;
                    const y = (event.clientY - rect.top) * chartHeight / rect.height;
                    setMouseGuideY(x >= margin.left && x <= margin.left + plotInnerWidth
                      && y >= margin.top && y <= volumePaneBottom ? y : null);
                  }}
                  onMouseLeave={() => setMouseGuideY(null)}
                >
                  {sessionSpans.map((span, idx) => {
                    const meta = sessionMeta[span.session] || sessionMeta.after;

                    const startX = margin.left + span.start * xStep - xStep / 2;
                    const endX = margin.left + span.end * xStep + xStep / 2;
                    const safeStartX = Math.max(margin.left, startX);
                    const safeEndX = Math.min(margin.left + plotInnerWidth, endX);
                    const width = Math.max(0, safeEndX - safeStartX);
                    if (width <= 0) return null;
                    const candleCount = plot.visibleSessionCounts?.[`${span.session}|${span.date}`]
                      || span.end - span.start + 1;
                    const description = `${meta.label} · ${span.date} · ${candleCount} candle${candleCount === 1 ? '' : 's'} in view. Session width reflects available candles.`;
                    const compactLabel = { pre: 'PM', market: 'RTH', after: 'AH' }[span.session] || meta.label;
                    const centerX = (safeStartX + safeEndX) / 2;

                    return (
                      <g key={`session-${idx}-${span.session}`} aria-label={description}>
                        <title>{description}</title>
                        <rect
                          x={safeStartX}
                          y={margin.top}
                          width={width}
                          height={pricePaneHeight}
                          fill={meta.fill}
                          opacity={span.session === 'market' ? 0.2 : 0.4}
                        />
                        {width > 72 ? (
                          <text
                            data-session={span.session}
                            data-session-date={span.date}
                            x={safeStartX + 6}
                            y={margin.top + 12}
                            fontSize="10"
                            fill={meta.text}
                            fontWeight="700"
                          >
                            <tspan x={safeStartX + 6}>{meta.label}</tspan>
                            <tspan x={safeStartX + 6} dy="12" fontSize="9" fontWeight="400">{span.date}</tspan>
                          </text>
                        ) : (
                          <text
                            data-session={span.session}
                            data-session-date={span.date}
                            x={centerX}
                            y={margin.top + 12}
                            transform={`rotate(90 ${centerX} ${margin.top + 12})`}
                            fontSize={width < 10 ? '7' : '9'}
                            fill={meta.text}
                            fontWeight="700"
                          >
                            {compactLabel}
                          </text>
                        )}

                        {span.session === 'market' ? (
                          <>
                            <line
                              x1={safeStartX}
                              y1={margin.top}
                              x2={safeStartX}
                              y2={margin.top + pricePaneHeight}
                              stroke="#1d4ed8"
                              strokeWidth="1.2"
                              strokeDasharray="4 4"
                              opacity="0.9"
                            />
                            <line
                              x1={safeEndX}
                              y1={margin.top}
                              x2={safeEndX}
                              y2={margin.top + pricePaneHeight}
                              stroke="#1d4ed8"
                              strokeWidth="1.2"
                              strokeDasharray="4 4"
                              opacity="0.9"
                            />
                          </>
                        ) : null}
                      </g>
                    );
                  })}

                  {gridLines.map((line, idx) => (
                    <g key={`grid-${idx}`}>
                      <line
                        x1={margin.left}
                        y1={line.y}
                        x2={margin.left + plotInnerWidth}
                        y2={line.y}
                        stroke="#e5e7eb"
                        strokeDasharray="3 3"
                      />
                    </g>
                  ))}

                  <rect
                    x={margin.left + plotInnerWidth + PRICE_LABEL_WIDTH}
                    y={margin.top}
                    width={MEASUREMENT_LANE_WIDTH}
                    height={pricePaneHeight}
                    fill="#ffffff"
                    pointerEvents="none"
                  />
                  {[
                    { key: 'target', measurement: entryTargetMeasurement, endY: entryTargetMeasurement?.targetY, price: targetPrice },
                    { key: 'stop', measurement: entryStopMeasurement, endY: entryStopMeasurement?.stopY, price: stopPrice },
                  ].map(({ key, measurement, endY, price }) => {
                    if (!measurement) return null;
                    const { entryY, offScale, percentage } = measurement;
                    const x = measurementColumnX;
                    const color = lineColorByKey[key];
                    const labelY = Math.max(
                      margin.top + 18,
                      Math.min(margin.top + pricePaneHeight - 18, (entryY + endY) / 2)
                    );
                    const direction = endY >= entryY ? 1 : -1;
                    const arrowSize = Math.min(5, Math.abs(endY - entryY) / 2);
                    const gapTop = labelY - 8;
                    const gapBottom = labelY + (offScale ? 20 : 8);
                    const lineTop = Math.min(entryY, endY);
                    const lineBottom = Math.max(entryY, endY);
                    return (
                      <g key={`measurement-${key}`} pointerEvents="none">
                        <title>{`Entry ${entryPrice} → ${lineLabelByKey[key]} ${price}: ${percentage}${offScale ? ' (off-scale)' : ''}`}</title>
                        {lineTop < gapTop && (
                          <line
                            x1={x} y1={lineTop} x2={x} y2={Math.min(lineBottom, gapTop)}
                            stroke={color} strokeWidth="1"
                          />
                        )}
                        {lineBottom > gapBottom && (
                          <line
                            x1={x} y1={Math.max(lineTop, gapBottom)} x2={x} y2={lineBottom}
                            stroke={color} strokeWidth="1"
                          />
                        )}
                        {(entryY < gapTop || entryY > gapBottom) && <line
                          x1={x - 5} y1={entryY} x2={x + 5} y2={entryY}
                          stroke={lineColorByKey.entry} strokeWidth="1"
                        />}
                        {(endY + arrowSize < gapTop || endY - arrowSize > gapBottom) && <polyline
                          points={`${x - arrowSize},${endY - direction * arrowSize} ${x},${endY} ${x + arrowSize},${endY - direction * arrowSize}`}
                          fill="none" stroke={color} strokeWidth="1"
                        />}
                        <text
                          x={x} y={labelY} textAnchor="middle" dominantBaseline="central"
                          fontSize="10" fontWeight="400" fill={color}
                        >
                          {percentage}
                          {offScale && (
                            <tspan x={x} dy="12" fontSize="9" fontWeight="400">
                              (off-scale)
                            </tspan>
                          )}
                        </text>
                      </g>
                    );
                  })}

                  {plot.smaLines.flatMap(({ period, color, segments }) => (
                    segments.map((segment, segmentIndex) => (
                      <polyline
                        key={`sma-${period}-${segmentIndex}`}
                        aria-label={`SMA ${period}`}
                        points={segment.map(({ index, value }) => (
                          `${margin.left + index * xStep},${clampY(yScale(value))}`
                        )).join(' ')}
                        fill="none"
                        stroke={color}
                        strokeWidth="1"
                        strokeLinecap="butt"
                        strokeLinejoin="miter"
                        pointerEvents="none"
                      >
                        <title>{`SMA ${period}`}</title>
                      </polyline>
                    ))
                  ))}

                  {liveLevel && (
                    <line
                      x1={liveLevel.x}
                      y1={liveLevel.y}
                      x2={liveLevel.labelX}
                      y2={liveLevel.y}
                      stroke={liveLevel.color}
                      strokeWidth="1"
                      strokeDasharray="1 4"
                      strokeLinecap="round"
                      pointerEvents="none"
                    />
                  )}

                  {plot.overlays.map((overlay) => {
                    const rawY = yScale(overlay.value);
                    const y = clampY(rawY);
                    const isOffScale = rawY !== y;
                    return (
                      <g key={overlay.key}>
                        <line
                          x1={margin.left}
                          y1={y}
                          x2={margin.left + plotInnerWidth}
                          y2={y}
                          stroke={lineColorByKey[overlay.key]}
                          strokeWidth="1.3"
                          strokeDasharray="6 4"
                        />
                        {(overlay.key === 'stop' || overlay.key === 'target') && (
                          <line
                            x1={margin.left + plotInnerWidth + PRICE_LABEL_WIDTH}
                            y1={y}
                            x2={measurementColumnX}
                            y2={y}
                            stroke={lineColorByKey[overlay.key]}
                            strokeWidth="1.3"
                            strokeDasharray="6 4"
                          />
                        )}
                        <text
                          x={margin.left + plotInnerWidth + 8}
                          y={y + 2.5}
                          fontSize="10.5"
                          fill={lineColorByKey[overlay.key]}
                          fontWeight="700"
                        >
                          <tspan x={margin.left + plotInnerWidth + 8}>{lineLabelByKey[overlay.key]} </tspan>
                          <tspan x={margin.left + plotInnerWidth + 8} dy="12">
                            {overlay.value.toFixed(2)}
                          </tspan>
                          {isOffScale && (
                            <tspan x={margin.left + plotInnerWidth + 8} dy="12">
                              (off-scale)
                            </tspan>
                          )}
                        </text>
                      </g>
                    );
                  })}

                  {setMarker ? (
                    <g>
                      <line
                        x1={setMarker.x}
                        y1={margin.top}
                        x2={setMarker.x}
                        y2={volumePaneBottom}
                        stroke="#7c3aed"
                        strokeWidth="1.4"
                        strokeDasharray="5 4"
                        opacity="0.95"
                      />
                    </g>
                  ) : null}

                  <line
                    x1={margin.left}
                    y1={volumePaneTop}
                    x2={margin.left + plotInnerWidth}
                    y2={volumePaneTop}
                    stroke="#e5e7eb"
                  />

                  {plot.candles.map((candle, idx) => {
                    const x = margin.left + idx * xStep;
                    const open = toFinite(candle.open);
                    const close = toFinite(candle.close);
                    const volume = Math.max(0, toFinite(candle.volume) || 0);
                    if (open === null || close === null) return null;

                    const up = close >= open;
                    const y = volumeScale(volume);

                    return (
                      <rect
                        key={`v-${idx}`}
                        x={x - volumeBarWidth / 2}
                        y={y}
                        width={volumeBarWidth}
                        height={Math.max(1, volumePaneBottom - y)}
                        fill={up ? '#34d399' : '#f87171'}
                        opacity="0.75"
                      />
                    );
                  })}

                  <text x={margin.left + 4} y={volumePaneBottom - 4} fontSize="11" fill="#6b7280">
                    {plot.volumeMax ? Math.round(plot.volumeMax).toLocaleString() : '0'}
                  </text>

                  {plot.candles.map((candle, idx) => {
                    const x = margin.left + idx * xStep;
                    const open = toFinite(candle.open);
                    const close = toFinite(candle.close);
                    const high = toFinite(candle.high);
                    const low = toFinite(candle.low);
                    if ([open, close, high, low].some((v) => v === null)) return null;

                    const up = close >= open;
                    const color = up ? '#16a34a' : '#dc2626';
                    const yOpen = clampY(yScale(open));
                    const yClose = clampY(yScale(close));
                    const bodyMid = (yOpen + yClose) / 2;
                    const rawBodyHeight = Math.abs(yClose - yOpen);
                    const bodyHeight = Math.max(MIN_BODY_HEIGHT_PX, rawBodyHeight);
                    const bodyTop = bodyMid - bodyHeight / 2;
                    const bodyBottom = bodyTop + bodyHeight;

                    let yHigh = clampY(yScale(high));
                    let yLow = clampY(yScale(low));
                    if (Math.abs(yLow - yHigh) < MIN_WICK_HEIGHT_PX) {
                      yHigh = bodyMid - MIN_WICK_HEIGHT_PX / 2;
                      yLow = bodyMid + MIN_WICK_HEIGHT_PX / 2;
                    }

                    // Ensure visible wick outside the body for ultra-tight candles.
                    yHigh = Math.min(yHigh, bodyTop - MIN_WICK_OUTSIDE_BODY_PX);
                    yLow = Math.max(yLow, bodyBottom + MIN_WICK_OUTSIDE_BODY_PX);
                    yHigh = clampY(yHigh);
                    yLow = clampY(yLow);

                    if (yLow <= yHigh) {
                      const half = Math.max(MIN_WICK_HEIGHT_PX / 2, MIN_WICK_OUTSIDE_BODY_PX + 1);
                      yHigh = clampY(bodyMid - half);
                      yLow = clampY(bodyMid + half);
                    }

                    return (
                      <g
                        key={`c-${idx}`}
                        data-candle-time={candle.time}
                        onMouseEnter={() => {
                          setHoveredCandle({
                            x,
                            y: bodyTop,
                            open,
                            close,
                            high,
                            low,
                            volume: Math.max(0, toFinite(candle.volume) || 0),
                            time: candle.time,
                          });
                        }}
                        onMouseLeave={() => setHoveredCandle(null)}
                      >
                        <rect
                          x={Math.max(margin.left, x - xStep / 2)}
                          y={margin.top}
                          width={(idx === plot.candles.length - 1
                            ? margin.left + plotInnerWidth
                            : x + xStep / 2)
                            - Math.max(margin.left, x - xStep / 2)}
                          height={volumePaneBottom - margin.top}
                          fill="transparent"
                        />
                        <rect
                          x={x - bodyWidth / 2}
                          y={bodyTop}
                          width={bodyWidth}
                          height={bodyHeight}
                          fill={up ? '#bbf7d0' : '#fecaca'}
                          stroke={color}
                          strokeWidth="1"
                        />
                        {[
                          [yHigh, bodyTop],
                          [bodyBottom, yLow],
                        ].map(([wickStart, wickEnd], wickIndex) => (
                          wickStart === wickEnd ? null : (
                            <line
                              key={`wick-${wickIndex}`}
                              x1={x}
                              y1={wickStart}
                              x2={x}
                              y2={wickEnd}
                              stroke={color}
                              strokeWidth="1"
                              strokeLinecap="butt"
                            />
                          )
                        ))}
                      </g>
                    );
                  })}

                  {liveLevel && (
                    <g pointerEvents="none">
                      <text
                        x={liveLevel.labelX}
                        y={liveLevel.y + 4}
                        textAnchor="start"
                        fontSize="11"
                        fontWeight="400"
                        fill={liveLevel.color}
                      >
                        <tspan x={liveLevel.labelX}>Live </tspan>
                        <tspan x={liveLevel.labelX} dy="12">{liveLevel.price}</tspan>
                      </text>
                    </g>
                  )}

                  {[
                    ...(entryMarker ? [{ ...entryMarker, label: 'Buy', color: '#2563eb' }] : []),
                    ...(plot.breakoutIndex >= 0 ? [{
                      index: plot.breakoutIndex,
                      x: margin.left + plot.breakoutIndex * xStep,
                      label: 'BO',
                      color: '#7c3aed',
                    }] : []),
                  ].map((marker) => {
                    const isCall = (scan?.signal_type || chartData?.signal_type) !== 'PUT';
                    const ex = marker.x;
                    const candle = plot.candles[marker.index];
                    const candleEdge = toFinite(isCall ? candle?.high : candle?.low);
                    if (candleEdge === null) return null;
                    const direction = isCall ? -1 : 1;
                    const sameCandleOffset = marker.label === 'BO' && marker.index === entryMarker?.index ? 34 : 0;
                    const arrowTipY = clampY(yScale(candleEdge)) + direction * (12 + sameCandleOffset);
                    const arrowBaseY = arrowTipY + direction * 9;
                    const arrowTailY = arrowBaseY + direction * 7;
                    const labelY = arrowTailY + (isCall ? -4 : 12);
                    return (
                      <g key={marker.label} aria-label={`${marker.label} candle marker`} pointerEvents="none">
                        <line
                          x1={ex}
                          y1={arrowTailY}
                          x2={ex}
                          y2={arrowBaseY}
                          stroke={marker.color}
                          strokeWidth="2"
                        />
                        <polygon
                          points={`${ex - 4},${arrowBaseY} ${ex + 4},${arrowBaseY} ${ex},${arrowTipY}`}
                          fill={marker.color}
                        >
                          <title>{marker.label === 'Buy' ? `Buy at ${marker.price.toFixed(4)}` : `Breakout ${scan.breakout_time} ET`}</title>
                        </polygon>
                        <text x={ex} y={labelY} textAnchor="middle" fontSize="9" fill={marker.color} fontWeight="700">
                          {marker.label}
                        </text>
                      </g>
                    );
                  })}

                  {mouseGuideY !== null && (
                    <g pointerEvents="none">
                      <line
                        aria-label="Mouse horizontal guide"
                        pointerEvents="none"
                        x1={margin.left}
                        x2={mouseGuidePrice !== null ? liveLabelX - 6 : margin.left + plotInnerWidth}
                        y1={mouseGuideY}
                        y2={mouseGuideY}
                        stroke="#2563eb"
                        strokeWidth="1.5"
                        strokeDasharray="1 4"
                        strokeLinecap="round"
                      />
                      {mouseGuidePrice !== null && (
                        <g aria-label="Mouse guide price">
                          <rect
                            x={liveLabelX - 6}
                            y={mouseGuideY - 10}
                            width={mouseGuidePrice.length * 7 + 12}
                            height={20}
                            rx="3"
                            fill="#2563eb"
                          />
                          <text
                            x={liveLabelX}
                            y={mouseGuideY}
                            dominantBaseline="central"
                            fontFamily="monospace"
                            fontSize="11"
                            fontWeight="600"
                            fill="#ffffff"
                          >
                            {mouseGuidePrice}
                          </text>
                        </g>
                      )}
                    </g>
                  )}
                  {hoveredCandle && (
                    <g pointerEvents="none">
                      <line
                        x1={hoveredCandle.x}
                        y1={margin.top}
                        x2={hoveredCandle.x}
                        y2={volumePaneBottom}
                        stroke="#9ca3af"
                        strokeDasharray="4 4"
                      />
                      {(() => {
                        const height = 64;
                        const y = volumePaneTop + 4;
                        const candleColor = hoveredCandle.close >= hoveredCandle.open ? '#86efac' : '#fca5a5';
                        const rows = [
                          { label: `${formatTsLong(hoveredCandle.time)} ET`, color: '#f9fafb' },
                          { label: `Opened: ${hoveredCandle.open.toFixed(2)}  Closed: ${hoveredCandle.close.toFixed(2)}`, color: candleColor },
                          { label: `High: ${hoveredCandle.high.toFixed(2)}  Low: ${hoveredCandle.low.toFixed(2)}`, color: candleColor },
                          { label: `Volume: ${Math.round(hoveredCandle.volume).toLocaleString()}`, color: candleColor },
                        ];
                        const width = Math.max(116, Math.max(...rows.map(row => row.label.length)) * 6 + 12);
                        const x = Math.round(Math.max(8, Math.min(svgWidth - width - 8, hoveredCandle.x - width / 2)));
                        return (
                          <g role="tooltip" aria-label="Candle information">
                            <rect x={x} y={y} width={width} height={height} rx="4" fill="#111827" />
                            {rows.map((row, index) => (
                              <text
                                key={index}
                                x={x + 6}
                                y={y + 13 + index * 14}
                                fontSize="10"
                                fontFamily="monospace"
                                fontWeight="600"
                                fill={row.color}
                                textRendering="geometricPrecision"
                              >
                                {row.label}
                              </text>
                            ))}
                          </g>
                        );
                      })()}
                    </g>
                  )}

                </svg>
              </div>

              <div className="grid grid-cols-[minmax(0,1fr)_25%_minmax(0,1fr)] items-center">
                <input
                  type="range"
                  min={0}
                  max={panMaxBars}
                  step={1}
                  value={sliderValue}
                  onChange={handleScrollBarChange}
                  disabled={panMaxBars <= 0}
                  className="col-start-2 w-full min-w-0 accent-blue-600"
                  aria-label="Chart horizontal scroll"
                />
                <p className="col-start-3 min-w-0 break-words pl-2 text-right text-xs text-gray-500">
                  Scan #{scan.id}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
